// React hook that reads/writes per-user UI preferences.
//
// Strategy:
// - Initial render uses an optimistic value from localStorage so the UI
//   doesn't flicker between "standard" and the user's actual choice while
//   the backend roundtrip is in flight.
// - On mount we fetch the authoritative values from the backend and
//   reconcile (server wins).
// - setUiKey() updates local state + cache immediately and PUTs the change
//   to the backend with a small debounce so rapid toggles coalesce.
//
// Server contract is described in backend/routes/user_preferences_routes.py.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';

import {
  fetchUserPreferences,
  updateUserPreferences,
} from '../helper/userPreferencesApi';

const LS_KEY = 'userPreferences:ui';
const DEFAULT_UI = { transcription_view_mode: 'standard' };
const SAVE_DEBOUNCE_MS = 400;

function readCache() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeCache(ui) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(ui));
  } catch {
    // localStorage may be unavailable (Safari private mode, quota); ignore.
  }
}

export function useUserPreferences() {
  const { instance } = useMsal();
  const [ui, setUi] = useState(() => ({
    ...DEFAULT_UI,
    ...(readCache() || {}),
  }));
  const [loaded, setLoaded] = useState(false);
  const debounceRef = useRef(null);

  // Initial load from backend; reconcile optimistic cache.
  useEffect(() => {
    let cancelled = false;
    fetchUserPreferences(instance)
      .then((data) => {
        if (cancelled) return;
        const merged = { ...DEFAULT_UI, ...(data?.ui || {}) };
        setUi(merged);
        writeCache(merged);
      })
      .catch((err) => {
        // Network/auth failures keep the optimistic cache active.
        // eslint-disable-next-line no-console
        console.warn('[useUserPreferences] backend fetch failed', err?.message || err);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setUiKey = useCallback(
    (key, value) => {
      setUi((prev) => {
        if (prev[key] === value) return prev;
        const next = { ...prev, [key]: value };
        writeCache(next);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          updateUserPreferences(instance, { [key]: value }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error(
              '[useUserPreferences] persist failed',
              err?.message || err,
            );
          });
        }, SAVE_DEBOUNCE_MS);
        return next;
      });
    },
    [instance],
  );

  // Flush pending debounce on unmount so a toggle made just before navigation
  // doesn't get lost.
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return { ui, setUiKey, loaded };
}
